import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Grid,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Box,
  Avatar,
} from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import CustomTextField from "../custom/CustomTextField";
import CustomSelectField from "../custom/CustomSelectField";
import CustomDatePicker from "../custom/CustomDatePicker";
import CustomNumberField from "../custom/CustomNumberField";
import CustomButton from "../custom/CustomButton";
import CustomAutocompleteField from "../custom/CustomAutoCompleteField";
import PhotoCropUploadModal from "../custom/PhotoCropUploadModal";
import { isBlank, isKtpValid, isNidnValid } from "../../utils/ValidationRules";
import { useUser } from "../../pages/UserContext";
import apiClient from "../../utils/apiClient";
import dayjs from "dayjs";
import Swal from "../../utils/swal";
import { showSuccessToast, showErrorToast } from "../../utils/swalToast";

const EditProfileModal = ({ open, onClose }) => {
  const userDetails = useUser();

  const [role, setRole] = useState("");
  const [ktp, setKtp] = useState("");
  const [namaLengkap, setNamaLengkap] = useState("");
  const [gelarDepan, setGelarDepan] = useState("");
  const [gelarBelakang, setGelarBelakang] = useState("");
  const [tempatLahir, setTempatLahir] = useState("");
  const [tanggalLahir, setTanggalLahir] = useState("");
  const [negaraAsal, setNegaraAsal] = useState("");
  const [provinsiInstitusi, setProvinsiInstitusi] = useState("");
  const [kotaInstitusi, setKotaInstitusi] = useState("");
  const [namaInstitusi, setNamaInstitusi] = useState("");
  const [jenjangMengajar, setJenjangMengajar] = useState("");
  const [pendidikanTerakhir, setPendidikanTerakhir] = useState("");
  const [jenjangAkademik, setJenjangAkademik] = useState("");
  const [bidangKeahlian1, setBidangKeahlian1] = useState("");
  const [bidangKeahlian2, setBidangKeahlian2] = useState("");
  const [bidangKeahlian3, setBidangKeahlian3] = useState("");

  const [nidn, setNidn] = useState("");
  const [hasNidn, setHasNidn] = useState(false);

  const [nomorHandphone, setNomorHandphone] = useState("");
  const [programStudi, setProgramStudi] = useState("");
  const [googleScholarId, setGoogleScholarId] = useState("");

  const [negaraAsalItems, setNegaraAsalItems] = useState([]);
  const [provinsiInstitusiItems, setProvinsiInstitusiItems] = useState([]);
  const [kotaInstitusiItems, setKotaInstitusiItems] = useState([]);
  const [namaInstitusiItems, setNamaInstitusiItems] = useState([]);
  const [jenjangMengajarItems, setJenjangMengajarItems] = useState([]);
  const [pendidikanTerakhirItems, setPendidikanTerakhirItems] = useState([]);
  const [jenjangAkademikItems, setJenjangAkademikItems] = useState([]);

  // Photo upload — cropping only stages the photo locally (pendingPhotoBlob);
  // it's only actually sent to the server when the main Update button is
  // pressed, bundled together with the rest of the form.
  const [currentPhotoUrl, setCurrentPhotoUrl] = useState("");
  const [selectedImageSrc, setSelectedImageSrc] = useState(null);
  const [openCropModal, setOpenCropModal] = useState(false);
  const [pendingPhotoBlob, setPendingPhotoBlob] = useState(null);
  const [pendingPhotoPreviewUrl, setPendingPhotoPreviewUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (!open) {
      setPendingPhotoBlob(null);
      setPendingPhotoPreviewUrl(null);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      try {
        const [
          countries,
          jenjangMengajar,
          pendidikanTerakhir,
          jenjangAkademik,
        ] = await Promise.all([
          apiClient.get("/countries"),
          apiClient.get("/teaching_levels"),
          apiClient.get("/educations"),
          apiClient.get("/academic_levels"),
        ]);

        setNegaraAsalItems(countries.data.data);
        setJenjangMengajarItems(jenjangMengajar.data.data);
        setPendidikanTerakhirItems(pendidikanTerakhir.data.data);
        setJenjangAkademikItems(jenjangAkademik.data.data);

        if (negaraAsal) {
          const provinces = await apiClient.get("/provinces");
          setProvinsiInstitusiItems(provinces.data.data);
        }

        if (provinsiInstitusi) {
          const regencies = await apiClient.get("/regencies");
          setKotaInstitusiItems(regencies.data.data);
        }

        if (kotaInstitusi) {
          const institutions = await apiClient.get("/institutions");
          setNamaInstitusiItems(institutions.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    fetchData();
  }, [negaraAsal, provinsiInstitusi, kotaInstitusi, open]);

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      try {
        const response = await apiClient.get("/users/details/all");
        const data = response.data.data;
        const provinceDetail = await apiClient.get(
          `/province?name=${data.provinsi_institusi}`
        );
        const date = dayjs(data.tanggal_lahir);
        setRole(data.role);
        setKtp(data.ktp);
        setNamaLengkap(data.nama_lengkap);
        setGelarDepan(data.gelar_depan);
        setGelarBelakang(data.gelar_belakang);
        setTempatLahir(data.tempat_lahir);
        setTanggalLahir(date);
        setNegaraAsal(data.negara_asal);
        setProvinsiInstitusi(provinceDetail.data.data.id);
        setKotaInstitusi(data.kota_institusi);
        setNamaInstitusi(data.institusi_asal);
        setJenjangMengajar(data.jenjang);
        setPendidikanTerakhir(data.pendidikan);
        setJenjangAkademik(data.jenjang_akademik);
        setBidangKeahlian1(data.keahlian_1);
        setBidangKeahlian2(data.keahlian_2);
        setBidangKeahlian3(data.keahlian_3);
        setNidn(data.nidn);
        setHasNidn(data.has_nidn);
        setGoogleScholarId(data.google_scholar_id);
        setNomorHandphone(data.no_hp);
        setProgramStudi(data.prodi);

        const rawFoto = data.foto;
        setCurrentPhotoUrl(
          rawFoto && rawFoto !== "null"
            ? `${process.env.REACT_APP_BACKEND_HOST}/${rawFoto}`
            : `${process.env.REACT_APP_BACKEND_HOST}/images/photo_profile/ikdki.png`
        );
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    };
    fetchData();
  }, [open]);

  const requestUpdate = async (e) => {
    e.preventDefault();
    Swal.fire({
      text: "Lakukan pembaharuan data ?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Ya",
      cancelButtonText: "Tidak",
      confirmButtonColor: "#003158",
      cancelButtonColor: "#A92027",
      didOpen: () => {
        document.querySelector(".swal2-container").style.zIndex = "9999";
        document.querySelector(".swal2-popup").style.zIndex = "99999";
      },
    }).then(async (result) => {
      if (!result.isConfirmed) return;

      try {
        const provinceLabel = await apiClient.get(
          `/provinces?id=${provinsiInstitusi}`
        );

        const requestPromise = apiClient.post(
          "/users/request",
          {
            namaLengkap: namaLengkap,
            gelarDepan: gelarDepan,
            gelarBelakang: gelarBelakang,
            tempatLahir: tempatLahir,
            tanggalLahir: tanggalLahir,
            namaInstitusi: namaInstitusi,
            kotaInstitusi: kotaInstitusi,
            provinsiInstitusi: provinceLabel.data.data[0].name,
            negaraAsal: negaraAsal,
            programStudi: programStudi,
            jenjangMengajar: jenjangMengajar,
            pendidikanTerakhir: pendidikanTerakhir,
            jenjangAkademik: jenjangAkademik,
            bidangKeahlian1: bidangKeahlian1,
            bidangKeahlian2: bidangKeahlian2,
            bidangKeahlian3: bidangKeahlian3,
            nidn: nidn,
            hasNidn: hasNidn,
            googleScholarId: googleScholarId,
            ktp: ktp,
            nomorHandphone: nomorHandphone,
          }
        );

        let photoPromise = Promise.resolve();
        if (pendingPhotoBlob) {
          const formData = new FormData();
          formData.append("foto", pendingPhotoBlob, "profile.jpg");
          formData.append("email", userDetails?.email);
          photoPromise = apiClient.put(
            "/users/profile-photo",
            formData,
            {
              headers: {
                "Content-Type": "multipart/form-data",
              },
            }
          );
        }

        await Promise.all([requestPromise, photoPromise]);

        showSuccessToast("Pembaharuan data berhasil diajukan");
        if (pendingPhotoBlob && userDetails?.refetchUser) {
          await userDetails.refetchUser();
        }
        onClose();
      } catch (err) {
        showErrorToast(err?.response?.data?.msg || "Gagal melakukan pembaharuan");
      }
    });
  };

  const handlePhotoFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedImageSrc(URL.createObjectURL(file));
      setOpenCropModal(true);
    }
    event.target.value = "";
  };

  const handleUploadProfilePhoto = () => {
    fileInputRef.current.click();
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Data Diri</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
          <Avatar src={pendingPhotoPreviewUrl || currentPhotoUrl} sx={{ width: 72, height: 72 }} />
          <CustomButton
            text="Ganti Foto"
            baseColor="#003158"
            hoverColor="#00213A"
            isFullWidth={false}
            onClick={handleUploadProfilePhoto}
          />
          {pendingPhotoPreviewUrl && (
            <Typography variant="caption" color="text.secondary">
              Foto baru akan tersimpan setelah menekan Update
            </Typography>
          )}
          <input
            type="file"
            accept="image/*"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={handlePhotoFileChange}
          />
        </Box>

        <Grid container spacing={2}>
          <Grid item md={6} xs={12}>
            <Typography>
              KTP<span style={{ color: "red" }}>*</span>
            </Typography>
            <CustomNumberField
              value={ktp}
              rule={isKtpValid}
              onChange={(event) => setKtp(event.target.value)}
              maxInputLength={16}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Typography>
              Nama Lengkap<span style={{ color: "red" }}>*</span>
            </Typography>
            <CustomTextField
              value={namaLengkap}
              rule={(value) => isBlank(value, "Nama Lengkap")}
              onChange={(event) => setNamaLengkap(event.target.value)}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Typography>Gelar Depan</Typography>
            <CustomTextField
              value={gelarDepan}
              onChange={(event) => setGelarDepan(event.target.value)}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Typography>Gelar Belakang</Typography>
            <CustomTextField
              value={gelarBelakang}
              onChange={(event) => setGelarBelakang(event.target.value)}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Typography>
              Tempat Lahir<span style={{ color: "red" }}>*</span>
            </Typography>
            <CustomTextField
              value={tempatLahir}
              rule={(value) => isBlank(value, "Tempat Lahir")}
              onChange={(event) => setTempatLahir(event.target.value)}
            />
          </Grid>
          <Grid item md={6} xs={12}>
            <Typography>
              Tanggal Lahir<span style={{ color: "red" }}>*</span>
            </Typography>
            <CustomDatePicker
              value={tanggalLahir}
              onChange={(event) => setTanggalLahir(event)}
              format={"DD/MM/YYYY"}
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography>Negara Asal</Typography>
            <CustomAutocompleteField
              value={negaraAsal}
              onChange={(newValue) => {
                setNegaraAsal(newValue ? newValue.code : "");
                setProvinsiInstitusi("");
                setKotaInstitusi("");
                setNamaInstitusi("");
              }}
              options={negaraAsalItems}
              placeholder="Pilih Negara Asal"
              searchBy="code"
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography>Provinsi Institusi</Typography>
            <CustomAutocompleteField
              value={provinsiInstitusi}
              onChange={(newValue) => {
                setProvinsiInstitusi(newValue ? newValue.id : "");
                setKotaInstitusi("");
                setNamaInstitusi("");
              }}
              disabled={negaraAsal === null || negaraAsal === ""}
              options={provinsiInstitusiItems}
              placeholder="Pilih Provinsi Institusi"
              searchBy="id"
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography>Kota Institusi</Typography>
            <CustomAutocompleteField
              value={kotaInstitusi}
              onChange={(newValue) => {
                setKotaInstitusi(newValue ? newValue.name : "");
                setNamaInstitusi("");
              }}
              disabled={provinsiInstitusi === "" || provinsiInstitusi === null}
              options={kotaInstitusiItems}
              placeholder="Pilih Kota Institusi"
              searchBy="name"
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography>Nama Institusi</Typography>
            <CustomAutocompleteField
              value={namaInstitusi}
              onChange={(newValue) => {
                setNamaInstitusi(newValue ? newValue.name : "");
              }}
              disabled={kotaInstitusi === "" || kotaInstitusi === null}
              options={namaInstitusiItems}
              placeholder="Pilih Institusi"
              searchBy="name"
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography>
              Program Studi<span style={{ color: "red" }}>*</span>
            </Typography>
            <CustomTextField
              value={programStudi}
              rule={(value) => isBlank(value, "Program Studi")}
              onChange={(event) => setProgramStudi(event.target.value)}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <Typography>
              Jenjang Mengajar<span style={{ color: "red" }}>*</span>
            </Typography>
            <CustomSelectField
              value={jenjangMengajar}
              onChange={(event) => setJenjangMengajar(event.target.value)}
              menuItems={jenjangMengajarItems.map((level, index) => (
                <MenuItem key={index} value={level.name}>
                  {level.name}
                </MenuItem>
              ))}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <Typography>
              Pendidikan Terakhir<span style={{ color: "red" }}>*</span>
            </Typography>
            <CustomSelectField
              value={pendidikanTerakhir}
              onChange={(event) => setPendidikanTerakhir(event.target.value)}
              menuItems={pendidikanTerakhirItems.map((lastEducation, index) => (
                <MenuItem key={index} value={lastEducation.name}>
                  {lastEducation.name}
                </MenuItem>
              ))}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <Typography>
              Jenjang Akademik<span style={{ color: "red" }}>*</span>
            </Typography>
            <CustomSelectField
              value={jenjangAkademik}
              onChange={(event) => setJenjangAkademik(event.target.value)}
              menuItems={jenjangAkademikItems.map((academicLevel, index) => (
                <MenuItem key={index} value={academicLevel.name}>
                  {academicLevel.name}
                </MenuItem>
              ))}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <Typography>Bidang Keahlian 1</Typography>
            <CustomTextField
              value={bidangKeahlian1}
              onChange={(event) => setBidangKeahlian1(event.target.value)}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <Typography>Bidang Keahlian 2</Typography>
            <CustomTextField
              value={bidangKeahlian2}
              onChange={(event) => setBidangKeahlian2(event.target.value)}
            />
          </Grid>
          <Grid item md={4} xs={12}>
            <Typography>Bidang Keahlian 3</Typography>
            <CustomTextField
              value={bidangKeahlian3}
              onChange={(event) => setBidangKeahlian3(event.target.value)}
            />
          </Grid>
          <Grid item md={12} xs={12}>
            {role === "Dosen" && (
              <>
                <Typography>
                  NIDN<span style={{ color: "red" }}>*</span>
                </Typography>

                <CustomNumberField
                  value={nidn}
                  onChange={(event) => setNidn(event.target.value)}
                  disabled={hasNidn === false}
                  rule={() => isNidnValid(role, nidn)}
                  maxInputLength={10}
                />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hasNidn === false}
                      onChange={(e) => setHasNidn(!e.target.checked)}
                    />
                  }
                  label="Saya belum memiliki NIDN"
                />
              </>
            )}
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography>
              Nomor Handphone<span style={{ color: "red" }}>*</span>
            </Typography>
            <CustomNumberField
              value={nomorHandphone}
              rule={(value) => isBlank(value, "Nomor Handphone")}
              onChange={(event) => setNomorHandphone(event.target.value)}
              maxInputLength={16}
            />
          </Grid>
          <Grid item md={12} xs={12}>
            <Typography>Google Scholar Id</Typography>
            <CustomTextField
              value={googleScholarId}
              onChange={(event) => setGoogleScholarId(event.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ padding: "15px 24px", gap: 1 }}>
        <CustomButton
          text="Batal"
          baseColor="#A92027"
          hoverColor="#8C1016"
          isFullWidth={false}
          onClick={onClose}
        />
        <CustomButton
          text="Update"
          baseColor="#003158"
          hoverColor="#00213A"
          isFullWidth={false}
          onClick={requestUpdate}
        />
      </DialogActions>

      <PhotoCropUploadModal
        open={openCropModal}
        onClose={() => setOpenCropModal(false)}
        imageSrc={selectedImageSrc}
        onCropped={(blob) => {
          setPendingPhotoBlob(blob);
          setPendingPhotoPreviewUrl(URL.createObjectURL(blob));
        }}
      />
    </Dialog>
  );
};

export default EditProfileModal;
